import { createAsyncThunk } from '@reduxjs/toolkit';

import { AsyncThunkConfig, PatientWithTotalCountResponseDto } from 'types/types';
import { getCaregiverPatients, getPatientsByFacility } from 'api/controllers/patient-controller';
import { getUserAge, getUserRoleTitle } from 'utils';
import { patientsActions } from '.';
import { UserRole } from 'types/enums';
import { deletePatientAndCaregiver, connectPatientAndCaregiver } from 'api/controllers/connect-controller';

type GetPatientsPayload = {
  id: string;
  limit?: number;
  skip?: number;
};

const connectPatientAndCaregiverConnectionByEmail = createAsyncThunk<void, string, AsyncThunkConfig>(
  'patients/connectPatientAndCaregiver',
  async (patientEmail, { dispatch, getState }) => {
    const { user } = getState();
    if (user.data?.role !== UserRole.CAREGIVER || !user.data.caregiverId) {
      return;
    }
    await connectPatientAndCaregiver(patientEmail);
    dispatch(patientsActions.getCaregiverPatientsData({ id: user.data.caregiverId }));
  },
);

const deletePatientAndCaregiverConnectionByEmail = createAsyncThunk<void, string, AsyncThunkConfig>(
  'patients/deletePatientAndCaregiverConnection',
  async (patientEmail, { dispatch, getState }) => {
    const { user } = getState();
    if (user.data?.role !== UserRole.CAREGIVER || !user.data.caregiverId) {
      return;
    }
    await deletePatientAndCaregiver(patientEmail);
    dispatch(patientsActions.getCaregiverPatientsData({ id: user.data.caregiverId }));
  },
);

const getCaregiverPatientsData = createAsyncThunk<PatientWithTotalCountResponseDto, GetPatientsPayload>(
  'patients/getCaregiverPatients',
  async ({ id: caregiverId, limit, skip }) => {
    const { patients, totalCount } = await getCaregiverPatients(caregiverId, skip, limit);

    const patientTableItem = patients.map(({ patient, firstName, lastName, email, role }) => ({
      id: patient.id,
      avatarUri: patient.avatarUri,
      fullName: `${firstName} ${lastName}`,
      firstName: firstName,
      lastName: lastName,
      email: email,
      gender: patient.gender ?? 'N/A',
      conditionals: patient.conditions,
      dateOfBirth: patient.dateOfBirth,
      role: getUserRoleTitle(role),
      ethnicity: patient.demographic ?? 'N/A',
      age: patient.dateOfBirth ? getUserAge(patient.dateOfBirth) : 'N/A',
      bloodPressure: 'TBD',
      waveform: 'TBD',
    }));

    return {
      patients: patientTableItem,
      totalCount,
    };
  },
);

const getPatientsByFacilityId = createAsyncThunk<PatientWithTotalCountResponseDto, GetPatientsPayload>(
  'patients/getPatientsByFacility',
  async ({ id: facilityId, limit, skip }) => {
    const { patients, totalCount } = await getPatientsByFacility(facilityId, skip, limit);

    const patientTableItem = patients.map(({ patient, firstName, lastName, email, role }) => ({
      id: patient.id,
      avatarUri: patient.avatarUri,
      fullName: `${firstName} ${lastName}`,
      firstName: firstName,
      lastName: lastName,
      email: email,
      gender: patient.gender ?? 'N/A',
      conditionals: patient.conditions,
      dateOfBirth: patient.dateOfBirth,
      role: getUserRoleTitle(role),
      ethnicity: patient.demographic ?? 'N/A',
      age: patient.dateOfBirth ? getUserAge(patient.dateOfBirth) : 'N/A',
      bloodPressure: 'TBD',
      waveform: 'TBD',
    }));

    return {
      patients: patientTableItem,
      totalCount,
    };
  },
);

export {
  getCaregiverPatientsData,
  connectPatientAndCaregiverConnectionByEmail,
  deletePatientAndCaregiverConnectionByEmail,
  getPatientsByFacilityId,
};
